import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowUpRight, FiCheckCircle, FiMail } from "react-icons/fi";
import "./Registration.css";

function Registration() {
  const { t } = useTranslation();
  const location = useLocation();

  const programConfigs = [
    {
      id: "afterschool-section",
      sectionKey: "afterschool",
      actionKey: "registerHere",
      learnLabelKey: "learn",
      metaKeys: ["openTo", "serving"],
      perksKey: null,
      tone: "teal",
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSfyUZt4peUyGezMs72Taa9NH0s-IayV7VJpHF8kV7YQbZ1tpg/viewform",
    },
    {
      id: "seniors-section",
      sectionKey: "seniors",
      actionKey: "registerHere",
      learnLabelKey: "learn",
      metaKeys: ["perfectFor", "serving"],
      perksKey: null,
      tone: "sage",
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSfyUZt4peUyGezMs72Taa9NH0s-IayV7VJpHF8kV7YQbZ1tpg/viewform",
    },
    {
      id: "codesoil-section",
      sectionKey: "codeAndSoil",
      actionKey: "registerHere",
      learnLabelKey: "learn",
      metaKeys: ["ages", "openTo"],
      perksKey: "standard",
      tone: "olive",
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSfyUZt4peUyGezMs72Taa9NH0s-IayV7VJpHF8kV7YQbZ1tpg/viewform",
    },
    {
      id: "ambassador-section",
      sectionKey: "ambassador",
      actionKey: "applyHere",
      learnLabelKey: "learnDo",
      metaKeys: ["youthAges", "openTo"],
      perksKey: "standard",
      tone: "violet",
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSc1wy9qQ3Hdiz7HipbozUxvNZwFAhSG4vsCtVtPmP_l8WIH9g/viewform",
    },
    {
      id: "internship-section",
      sectionKey: "internship",
      actionKey: "applyHere",
      learnLabelKey: "learnDo",
      metaKeys: ["youthAges", "openTo"],
      perksKey: "standard",
      tone: "cyan",
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSd20D8uhIulksZgq5NcD0Q1Jte71dKmUCCQFtHZ4fdI5PuAeQ/viewform",
    },
    {
      id: "techpal-section",
      sectionKey: "techPal",
      actionKey: "applyHere",
      learnLabelKey: "learnCollaborate",
      metaKeys: ["youthAges", "openTo"],
      perksKey: "standard",
      tone: "sky",
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSd20D8uhIulksZgq5NcD0Q1Jte71dKmUCCQFtHZ4fdI5PuAeQ/viewform",
    },
    {
      id: "workshops-section",
      sectionKey: "workshops",
      actionKey: "registerHere",
      learnLabelKey: "learnExplore",
      metaKeys: ["openTo"],
      perksKey: "workshops",
      tone: "amber",
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSfw79a85sHFn3zXedEE8bfWOwSPc82VZ28drPHTPhJm2eJG9w/viewform",
    },
    {
      id: "techcamps-section",
      sectionKey: "techCamps",
      actionKey: "registerHere",
      learnLabelKey: "learnExplore",
      metaKeys: ["openTo"],
      perksKey: "techCamps",
      tone: "sea",
      formUrl: "https://docs.google.com/forms/d/e/1FAIpQLSfyUZt4peUyGezMs72Taa9NH0s-IayV7VJpHF8kV7YQbZ1tpg/viewform",
    },
  ];

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        const nav = document.querySelector(".cf-navbar");
        const navHeight = nav ? nav.getBoundingClientRect().height : 96;
        const rect = el.getBoundingClientRect();
        const viewportContentHeight = Math.max(window.innerHeight - navHeight, 0);
        const centerOffset = Math.max((viewportContentHeight - rect.height) / 2, 0);
        const visualBias = Math.min(viewportContentHeight * 0.1, 120);
        const top = rect.top + window.scrollY - navHeight - centerOffset + visualBias;
        window.scrollTo({
          top: Math.max(top, 0),
          behavior: "smooth",
        });

        // Improve accessibility for hash navigation targets.
        el.setAttribute("tabindex", "-1");
        el.focus({ preventScroll: true });
      }
    }
  }, [location.hash]);

  const asArray = (key) => {
    const raw = t(key, { returnObjects: true });
    return Array.isArray(raw) ? raw : [];
  };

  const getTaglineLines = (sectionKey) => {
    const baseKey = `registration.sections.${sectionKey}`;
    const lineKeys = ["taglineLine1", "taglineLine2", "taglineLine3"];

    const lineValues = lineKeys
      .map((lineKey) => t(`${baseKey}.${lineKey}`, { defaultValue: "" }))
      .filter((line) => Boolean(line));

    if (lineValues.length > 0) {
      return lineValues;
    }

    const singleTagline = t(`${baseKey}.tagline`, { defaultValue: "" });
    return singleTagline ? [singleTagline] : [];
  };

  const openForm = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="registration-page" style={{ paddingTop: "6rem" }}>
      <div className="registration-page-bg" aria-hidden="true" />

      <header className="registration-hero">
        <div className="registration-hero-pill">{t("registration.header.title")}</div>
        <h1 className="registration-hero-title">{t("registration.ui.title")}</h1>
        <p className="registration-hero-subtitle">{t("registration.ui.subtitle")}</p>

        <div className="registration-hero-meta" role="list" aria-label={t("registration.ui.metaAria")}>
          <span className="registration-meta-chip" role="listitem">
            {t("registration.ui.programCount", { count: programConfigs.length })}
          </span>
          <a className="registration-meta-chip registration-meta-chip--link" href="mailto:info@choicestfruit.com">
            <FiMail aria-hidden="true" />
            <span>{t("registration.ui.contact", { email: "info@choicestfruit.com" })}</span>
          </a>
        </div>

        <nav className="registration-quick-nav" aria-label={t("registration.ui.quickNavAria")}>
          {programConfigs.map((program) => (
            <a key={program.id} href={`#${program.id}`} className="registration-quick-nav-item">
              {t(`registration.sections.${program.sectionKey}.title`)}
            </a>
          ))}
        </nav>
      </header>

      <main className="registration-main">
        {programConfigs.map((program, index) => {
          const baseKey = `registration.sections.${program.sectionKey}`;
          const taglineLines = getTaglineLines(program.sectionKey);
          const perkItems = program.perksKey ? asArray(`registration.perks.${program.perksKey}`) : [];
          const learnItems = asArray(`${baseKey}.learn`);

          return (
            <section id={program.id} key={program.id} className={`registration-card registration-card--${program.tone}`}>
              <div className="registration-card-top">
                <span className="registration-card-index">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h2 className="registration-card-title">{t(`${baseKey}.title`)}</h2>
                  <p className="registration-card-subtitle">{t(`${baseKey}.subtitle`, { defaultValue: "" })}</p>
                </div>
              </div>

              <div className="registration-card-grid">
                <div className="registration-card-primary">
                  <div className="registration-tagline-wrap">
                    {taglineLines.map((line) => (
                      <p key={line} className="registration-tagline-line">{line}</p>
                    ))}
                  </div>

                  <button type="button" onClick={() => openForm(program.formUrl)} className="registration-cta-btn">
                    <span>{t(`registration.buttons.${program.actionKey}`)}</span>
                    <FiArrowUpRight aria-hidden="true" />
                  </button>

                  {perkItems.length > 0 ? (
                    <ul className="registration-perks-list">
                      {perkItems.map((item, perkIndex) => (
                        <li key={`${program.id}-perk-${perkIndex}`}>
                          <FiCheckCircle className="registration-perk-icon" aria-hidden="true" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <a href="mailto:info@choicestfruit.com" className="registration-contact-line">
                    <FiMail aria-hidden="true" />
                    <span>info@choicestfruit.com</span>
                  </a>
                </div>

                <div className="registration-card-secondary">
                  <div className="registration-info-block">
                    <h3 className="registration-info-heading">{t(`registration.labels.${program.learnLabelKey}`)}</h3>
                    <ul className="registration-learn-list">
                      {learnItems.map((item, learnIndex) => (
                        <li key={`${program.id}-learn-${learnIndex}`}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="registration-info-block">
                    <h3 className="registration-info-heading">{t("registration.labels.whatItIs")}</h3>
                    <p className="registration-what-text">{t(`${baseKey}.whatItIs`)}</p>
                    {program.metaKeys.map((metaKey) => (
                      <p key={`${program.id}-${metaKey}`} className="registration-meta-line">
                        {t(`${baseKey}.${metaKey}`)}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </main>
    </div>
  );
}

export default Registration;
